import { createRoute } from '@tanstack/react-router'
import { useForm } from '@tanstack/react-form'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Mail } from 'lucide-react'
import { Button, Input } from '@arabquiz/ui'
import { Route as rootRoute } from '../__root'

/**
 * Forgot-password route — Arabic, RTL.
 *
 * Asks the user for the email address tied to their account and POSTs it to
 * `/api/v1/auth/forgot-password`. The backend always returns 200 (whether or
 * not the email exists) to prevent enumeration, so the UI flips to the same
 * "if it exists, we sent a link" message on every successful response.
 *
 * Visual style intentionally mirrors `sign-in.tsx` / `sign-up.tsx`: the same
 * brand-tinted gradient, `font-display` heading, recurring 1×12 accent bar.
 * The `<Input>` carries `dir="ltr"` because Latin emails must render
 * left-to-right even on an RTL page.
 */
export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/forgot-password',
  component: ForgotPasswordPage,
})

type ForgotPasswordPayload = {
  email: string
}

type ValidationKey = 'required' | 'invalidEmail' | 'passwordTooShort' | 'passwordMismatch'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function ForgotPasswordPage() {
  const { t } = useTranslation('auth')
  const { t: tValidation } = useTranslation('validation')
  const { t: tCommon } = useTranslation('common')

  const forgotMutation = useMutation({
    mutationFn: async (payload: ForgotPasswordPayload) => {
      const res = await fetch('/api/v1/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'include',
      })
      if (!res.ok) {
        const text = await res.text()
        throw new Error(text || `Forgot-password failed: ${res.status}`)
      }
      return res.json() as Promise<{ message: string }>
    },
  })

  const form = useForm({
    defaultValues: { email: '' } as ForgotPasswordPayload,
    onSubmit: async ({ value }) => {
      await forgotMutation.mutateAsync(value)
    },
  })

  // Success state: the API has accepted the request. Render the constant
  // "check your inbox" message regardless of whether the address exists.
  if (forgotMutation.isSuccess) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-gradient-to-bl from-brand-50/60 via-white to-brand-100/30 px-4 py-12">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-3xl shadow-xl border border-brand-100 p-8 sm:p-10 text-center">
            <div
              className="inline-block h-1 w-12 rounded-full bg-brand-500 mb-4"
              aria-hidden="true"
            />

            <div
              className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-brand-50 ring-1 ring-brand-200/60"
              aria-hidden="true"
            >
              <Mail className="h-10 w-10 text-brand-600" strokeWidth={1.5} />
            </div>

            <h1 className="text-2xl font-bold font-display text-brand-900 tracking-tight mb-3">
              {t('forgotPasswordTitle')}
            </h1>
            <p className="text-brand-800 leading-relaxed mb-6">
              {t('forgotPasswordSent')}
            </p>

            <a
              href="/auth/sign-in"
              className="inline-block font-semibold text-brand-600 hover:text-brand-700 hover:underline"
            >
              {t('backToSignIn')}
            </a>
          </div>
        </div>
      </main>
    )
  }

  return (
    <main className="min-h-screen flex items-center justify-center bg-gradient-to-bl from-brand-50/60 via-white to-brand-100/30 px-4 py-12">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-3xl shadow-xl border border-brand-100 p-8 sm:p-10">
          <div className="mb-8 text-center">
            <div
              className="inline-block h-1 w-12 rounded-full bg-brand-500 mb-4"
              aria-hidden="true"
            />
            <h1 className="text-3xl font-bold font-display text-brand-900 tracking-tight mb-3">
              {t('forgotPasswordTitle')}
            </h1>
            <p className="text-brand-800 text-sm leading-relaxed">
              {t('forgotPasswordDescription')}
            </p>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault()
              e.stopPropagation()
              void form.handleSubmit()
            }}
            noValidate
          >
            <div className="space-y-5">
              <form.Field
                name="email"
                validators={{
                  onChange: ({ value }) => {
                    if (!value) return 'required'
                    if (!EMAIL_PATTERN.test(value)) return 'invalidEmail'
                    return undefined
                  },
                }}
              >
                {(field) => {
                  const firstError = field.state.meta.errors[0]
                  const showError =
                    field.state.meta.isTouched && typeof firstError === 'string'
                  return (
                    <div>
                      <label
                        htmlFor={field.name}
                        className="block text-sm font-semibold text-brand-900 mb-1.5"
                      >
                        {t('email')}
                      </label>
                      <Input
                        id={field.name}
                        type="email"
                        name={field.name}
                        autoComplete="email"
                        dir="ltr"
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(e) => field.handleChange(e.target.value)}
                        aria-invalid={showError || undefined}
                        aria-describedby={showError ? `${field.name}-error` : undefined}
                      />
                      {showError ? (
                        <p
                          id={`${field.name}-error`}
                          className="text-xs text-red-600 mt-1.5"
                          role="alert"
                        >
                          {tValidation(firstError as ValidationKey)}
                        </p>
                      ) : null}
                    </div>
                  )
                }}
              </form.Field>

              {forgotMutation.isError ? (
                <div
                  className="rounded-md bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2"
                  role="alert"
                >
                  {tCommon('error')}
                </div>
              ) : null}

              <Button
                type="submit"
                size="lg"
                disabled={forgotMutation.isPending}
                className="w-full"
              >
                {forgotMutation.isPending ? tCommon('loading') : t('forgotPasswordSubmit')}
              </Button>
            </div>
          </form>

          <p className="mt-6 text-center text-sm text-brand-800">
            <a
              href="/auth/sign-in"
              className="font-semibold text-brand-600 hover:text-brand-700 hover:underline"
            >
              {t('backToSignIn')}
            </a>
          </p>
        </div>
      </div>
    </main>
  )
}
